/**
 * Password Validation Utility for PTS Project
 * Implements comprehensive password validation rules
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

export interface PasswordValidationOptions {
  // User context for restrictions
  username?: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  dateOfBirth?: string; // Format: YYYY-MM-DD or DDMMYYYY or MMDDYY
  currentPassword?: string; // For password change scenarios
}

// Allowed special characters
const ALLOWED_SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

// Common passwords to reject
const COMMON_PASSWORDS = [
  "Password@123",
  "Admin@123",
  "12345678",
  "Password123",
  "Admin123",
  "Password1",
  "Admin1",
  "Welcome123",
  "Welcome@123",
  "Test123",
  "Test@123",
];

// Common dictionary words (basic list - full validation should be done on backend)
const COMMON_DICTIONARY_WORDS = [
  "password",
  "admin",
  "welcome",
  "hello",
  "world",
  "happens",
  "good",
  "bad",
  "like",
  "love",
  "test",
  "user",
  "guest",
  "qwerty",
  "abc123",
];

// Sequential patterns to detect
const SEQUENTIAL_PATTERNS = [
  "abcdefghijklmnopqrstuvwxyz",
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  "0123456789",
  "qwertyuiopasdfghjklzxcvbnm",
  "QWERTYUIOPASDFGHJKLZXCVBNM",
];

/**
 * Check if password has leading or trailing spaces
 */
function hasLeadingOrTrailingSpaces(password: string): boolean {
  return password !== password.trim();
}

/**
 * Check if password contains only allowed special characters
 */
function hasInvalidSpecialChars(password: string): boolean {
  const specialCharRegex = /[^a-zA-Z0-9]/g;
  const specialChars = password.match(specialCharRegex);
  if (!specialChars) {
    return false;
  }

  return specialChars.some(char => !ALLOWED_SPECIAL_CHARS.includes(char));
}

/**
 * Check for repeating characters (more than 3 in a row)
 */
function hasRepeatingChars(password: string): boolean {
  let maxRepeat = 1;
  let currentRepeat = 1;
  let lastChar = "";

  for (const char of password) {
    if (char === lastChar) {
      currentRepeat++;
      maxRepeat = Math.max(maxRepeat, currentRepeat);
    } else {
      currentRepeat = 1;
    }
    lastChar = char;
  }

  return maxRepeat > 3;
}

/**
 * Check for sequential patterns (longer than 3 characters)
 */
function hasSequentialPattern(password: string): boolean {
  const lowerPassword = password.toLowerCase();

  for (const pattern of SEQUENTIAL_PATTERNS) {
    for (let i = 0; i <= pattern.length - 4; i++) {
      const sequence = pattern.substring(i, i + 4);
      const reverseSequence = sequence.split("").reverse().join("");

      if (
        lowerPassword.includes(sequence.toLowerCase()) ||
        lowerPassword.includes(reverseSequence.toLowerCase())
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if password is a common password
 */
function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.some(
    common => common.toLowerCase() === password.toLowerCase()
  );
}

/**
 * Check if password contains a dictionary word
 */
function containsDictionaryWord(password: string): boolean {
  const lowerPassword = password.toLowerCase();
  return COMMON_DICTIONARY_WORDS.some(word =>
    lowerPassword.includes(word.toLowerCase())
  );
}

/**
 * Extract date patterns from date of birth
 */
function extractDatePatterns(dateOfBirth: string): string[] {
  const patterns: string[] = [];

  if (!dateOfBirth) {
    return patterns;
  }

  // Remove dashes and slashes
  const cleanDate = dateOfBirth.replace(/[-/]/g, "");

  // YYYYMMDD format
  if (cleanDate.length === 8) {
    patterns.push(cleanDate); // YYYYMMDD
    patterns.push(cleanDate.substring(2)); // YYMMDD
    patterns.push(cleanDate.substring(4) + cleanDate.substring(0, 4)); // DDMMYYYY
    patterns.push(
      cleanDate.substring(4) +
        cleanDate.substring(2, 4) +
        cleanDate.substring(0, 2)
    ); // DDMMYY
  }

  // MMDDYYYY format
  if (cleanDate.length === 8) {
    patterns.push(
      cleanDate.substring(0, 2) +
        cleanDate.substring(2, 4) +
        cleanDate.substring(4)
    ); // MMDDYYYY
    patterns.push(
      cleanDate.substring(0, 2) +
        cleanDate.substring(2, 4) +
        cleanDate.substring(4, 6)
    ); // MMDDYY
  }

  return patterns;
}

/**
 * Check if password contains user information
 */
function containsUserInfo(
  password: string,
  options: PasswordValidationOptions
): string[] {
  const errors: string[] = [];
  const lowerPassword = password.toLowerCase();

  // Check username
  if (options.username) {
    const usernameLower = options.username.toLowerCase();
    if (lowerPassword.includes(usernameLower)) {
      errors.push("Password cannot contain your username");
    }
  }

  // Check email (extract local part)
  if (options.email) {
    const emailLower = options.email.toLowerCase();
    const emailParts = emailLower.split("@");
    if (
      emailParts[0] &&
      emailParts[0].length >= 3 &&
      lowerPassword.includes(emailParts[0])
    ) {
      errors.push("Password cannot contain your email address");
    }
  }

  // Check phone number (remove formatting)
  if (options.phoneNumber) {
    const phoneDigits = options.phoneNumber.replace(/\D/g, "");
    if (phoneDigits && password.includes(phoneDigits)) {
      errors.push("Password cannot contain your phone number");
    }
  }

  // Check first name
  if (options.firstName) {
    const firstNameLower = options.firstName.toLowerCase();
    if (firstNameLower.length >= 3 && lowerPassword.includes(firstNameLower)) {
      errors.push("Password cannot contain your first name");
    }
  }

  // Check last name
  if (options.lastName) {
    const lastNameLower = options.lastName.toLowerCase();
    if (lastNameLower.length >= 3 && lowerPassword.includes(lastNameLower)) {
      errors.push("Password cannot contain your last name");
    }
  }

  // Check middle name
  if (options.middleName) {
    const middleNameLower = options.middleName.toLowerCase();
    if (
      middleNameLower.length >= 3 &&
      lowerPassword.includes(middleNameLower)
    ) {
      errors.push("Password cannot contain your middle name");
    }
  }

  // Check date of birth patterns
  if (options.dateOfBirth) {
    const datePatterns = extractDatePatterns(options.dateOfBirth);
    for (const pattern of datePatterns) {
      if (password.includes(pattern)) {
        errors.push("Password cannot contain your date of birth");
        break;
      }
    }
  }

  return errors;
}

/**
 * Main password validation function
 */
export function validatePassword(
  password: string,
  options: PasswordValidationOptions = {}
): PasswordValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Length validation (8 to 10 characters)
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  if (password.length > 10) {
    errors.push("Password must be no more than 10 characters long");
  }

  // Character type validation
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one UPPER CASE letter (A-Z)");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lower case letter (a-z)");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number (0-9)");
  }
  if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
    errors.push(
      "Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)"
    );
  }

  // Format validation
  if (hasLeadingOrTrailingSpaces(password)) {
    errors.push("Password cannot have spaces at the beginning or end");
  }
  if (hasInvalidSpecialChars(password)) {
    errors.push(
      `Password can only contain these special characters: ${ALLOWED_SPECIAL_CHARS}`
    );
  }

  // Restrictions validation
  if (hasRepeatingChars(password)) {
    errors.push(
      "Password cannot contain more than 3 repeating characters in a row (e.g., aaaa, 1111)"
    );
  }
  if (hasSequentialPattern(password)) {
    errors.push(
      "Password cannot contain sequential patterns longer than 3 characters (e.g., abcd, 1234, qwerty)"
    );
  }
  if (isCommonPassword(password)) {
    errors.push(
      "Password cannot be a common password (e.g., Password@123, Admin@123, 12345678)"
    );
  }
  if (containsDictionaryWord(password)) {
    errors.push(
      "Password cannot contain dictionary words (like password, admin, welcome, etc.)"
    );
  }

  // User information restrictions
  const userInfoErrors = containsUserInfo(password, options);
  errors.push(...userInfoErrors);

  // Current password check (for password change scenarios)
  if (options.currentPassword && password === options.currentPassword) {
    errors.push("New password must be different from your current password");
  }

  // Note: Last 10 passwords check should be done on the backend
  // Frontend cannot access password history

  return {
    isValid: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Helper function to get password requirements text for UI display
 */
export function getPasswordRequirementsText(): string[] {
  return [
    "8 to 10 characters long",
    "At least one UPPER CASE letter (A-Z)",
    "At least one lower case letter (a-z)",
    "At least one number (0-9)",
    "At least one special character: !@#$%^&*()_+-=[]{}|;:,.<>?",
    "No spaces at the beginning or end",
    "No more than 3 repeating characters in a row",
    "No sequential patterns longer than 3 characters",
    "Not a common password or dictionary word",
    "Cannot contain your username, email, phone, name, or date of birth",
  ];
}
